import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { matchPath, Outlet, useLocation } from "react-router-dom";

import { styled, useTheme } from "@mui/material/styles";
import {
  AppBar,
  Avatar,
  CssBaseline,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";

import Header from "./Header";
import Sidebar from "./Sidebar";

import { drawerWidth, collapsedDrawerWidth } from "utils/constant";
import { setMenu } from "store/slices/customizationSlice";
import {
  removeUserDetails,
} from "utils/storage.utils";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";

const Main = styled("main", {
  shouldForwardProp: (prop) => !["open", "noSideBar"].includes(String(prop)),
})(({ theme, open, noSideBar }) => {
  const activeWidth = open ? drawerWidth : collapsedDrawerWidth;

  return {
    ...theme.typography.mainContent,
    transition: theme.transitions.create(["margin-left", "width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shorter,
    }),

    ...(noSideBar
      ? {
        marginLeft: 0,
        width: "100%",
        minHeight: "100vh",
        marginTop: 0,
      }
      : {
        marginLeft: activeWidth,
        width: `calc(100% - ${activeWidth}px)`,
      }),

    [theme.breakpoints.down("lg")]: {
      marginLeft: 0,
      width: "100%",
    },
  };
});

const MainLayout = () => {
  const theme = useTheme();
  const location = useLocation();


  const isQuestionRoute = matchPath("/assessment/:id", location.pathname);
  const isUserRoute = matchPath("/user/:id", location.pathname);
  const isPromptRoute = matchPath("/prompt/:id", location.pathname);
  const matchDownMd = useMediaQuery(theme.breakpoints.down("lg"));

  const leftDrawerOpened = useSelector((state) => state.customization?.opened);
  const { user } = useSelector((s) => s.auth || {});

  const dispatch = useDispatch();



  const handleLeftDrawerToggle = () => {
    dispatch(setMenu({ opened: !leftDrawerOpened }));
  };

  useEffect(() => {
    // open on desktop, collapse on mobile/tablet
    dispatch(setMenu({ opened: !matchDownMd }));
  }, [dispatch, matchDownMd]);

  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogout = () => {
    removeUserDetails();
  };

  return (
    <>
      <CssBaseline />
      {isQuestionRoute || isUserRoute || isPromptRoute ? (
        <Main noSideBar>
          <Outlet />
        </Main>
      ) : (
        <>
          <AppBar
            enableColorOnDark
            position="fixed"
            color="inherit"
            elevation={1}
            sx={{
              bgcolor: theme.palette.background.default,
              transition: theme.transitions.create("width"),
            }}
          >
            <Toolbar>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <Header handleLeftDrawerToggle={handleLeftDrawerToggle} />
                <IconButton onClick={handleClick} size="small">
                  <Avatar alt="Profile" />
                </IconButton>
              </Stack>
            </Toolbar>
          </AppBar>

          <Sidebar
            drawerOpen={leftDrawerOpened}
            drawerToggle={handleLeftDrawerToggle}
          />

          {/* ✅ pass open so Main uses collapsedDrawerWidth when closed */}
          <Main open={leftDrawerOpened}>
            <Outlet />
          </Main>

          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                minWidth: 180,
                borderRadius: 2,
              },
            }}
          >
            <MenuItem sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AccountCircleIcon color="primary" />
              <Typography variant="subtitle1" noWrap>
                {user?.name || "Anonymous"}
              </Typography>
            </MenuItem>

            <Divider sx={{ my: 1 }} />

            <MenuItem
              onClick={() => {
                handleLogout();
                handleClose();
              }}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <ExitToAppIcon color="error" />
              <Typography variant="subtitle1" noWrap>
                Logout
              </Typography>
            </MenuItem>
          </Menu>
        </>
      )}
    </>
  );
};

export default MainLayout;